import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import LogoutButton from "./components/LogoutButton.jsx";
import { useAuth } from "./contexts/AuthContext.jsx";
import ConfirmExclusionModal from "./components/ConfirmExclusionModal.jsx";
import { apiFetch, fetchMedecinsMaitre } from "./apiMedecins.js";
import { PLANNING_PR_MEDECINS_ENDPOINT, fetchPlanningPrConfig } from "./apiPlanningPr.js";
import { notify } from "./ui/dialogService.js";
import BourseTravailMedecin from "./BourseTravailMedecin.jsx";

// ===============================
// PortailMedecin.jsx
// ===============================
// Page d'accueil après connexion pour les médecins
// - Accès désidérata activité OPH
// - Accès interface médecins (gardes, listes, etc.)
// ===============================

export default function PortailMedecin() {
  const { user } = useAuth();
  const [medecinName, setMedecinName] = useState(null);
  const [medecinData, setMedecinData] = useState(null); // Données complètes du médecin depuis la liste maître
  const [participatingGardes, setParticipatingGardes] = useState(false);
  const [participatingGardesPr1, setParticipatingGardesPr1] = useState(false);
  const [participatingPlanningPRInterieur, setParticipatingPlanningPRInterieur] = useState(false);
  const [participatingGardesReanimation, setParticipatingGardesReanimation] = useState(false);
  const [participatingAstreinteThoracique, setParticipatingAstreinteThoracique] = useState(false);
  const [participatingAstreinteThoraciqueInterieur, setParticipatingAstreinteThoraciqueInterieur] = useState(false);
  const [participatingGardesDigestif, setParticipatingGardesDigestif] = useState(false);
  const [participatingPlanningDigestifInterieur, setParticipatingPlanningDigestifInterieur] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showExclusionModal, setShowExclusionModal] = useState({ open: false, activity: null });
  const [updating, setUpdating] = useState(false);
  const [portRoyalOpen, setPortRoyalOpen] = useState(false);
  const [thoraciqueOpen, setThoraciqueOpen] = useState(false);
  const [cfgPlanningPR, setCfgPlanningPR] = useState(null);
  const [cfgPlanningThoracique, setCfgPlanningThoracique] = useState(null);
  const [restoringSupportSession, setRestoringSupportSession] = useState(false);
  const [openingDigestifResponsable, setOpeningDigestifResponsable] = useState(false);
  const [multiSecteurAccess, setMultiSecteurAccess] = useState([]);
  const [loadingMultiSecteurAccess, setLoadingMultiSecteurAccess] = useState(false);
  const [echangesPilotEligible, setEchangesPilotEligible] = useState(false);
  const [bourseEligible, setBourseEligible] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setBourseEligible(false);
    if (user) apiFetch("/api/v2/bourse-travail/feature-status", { credentials:"include" })
      .then(async response => response.ok ? response.json() : {})
      .then(data => { if (!cancelled) setBourseEligible(data.enabled === true); })
      .catch(() => { if (!cancelled) setBourseEligible(false); });
    return () => { cancelled = true; };
  }, [user]);

  // Charger la config planning PR pour vérifier l'accès restreint
  useEffect(() => {
    fetchPlanningPrConfig()
      .then(cfg => setCfgPlanningPR(cfg))
      .catch(() => setCfgPlanningPR(null));
  }, []);

  useEffect(() => {
    apiFetch("/api/v2/config/desiderata-planning-thoracique")
      .then(r => r.ok ? r.json() : null)
      .then(cfg => setCfgPlanningThoracique(cfg))
      .catch(() => setCfgPlanningThoracique(null));
  }, []);

  useEffect(() => {
    const loadMultiSecteurAccess = async () => {
      try {
        setLoadingMultiSecteurAccess(true);
        const res = await apiFetch("/api/v2/multisecteur/access", { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.ok) {
          setMultiSecteurAccess([]);
          return;
        }
        const rows = Array.isArray(data.data) ? data.data : [];
        setMultiSecteurAccess(rows.filter((r) => String(r?.secteur_id || "").trim().length > 0));
      } catch (err) {
        console.warn("⚠️ Chargement accès multisecteur impossible:", err?.message || err);
        setMultiSecteurAccess([]);
      } finally {
        setLoadingMultiSecteurAccess(false);
      }
    };
    if (user) loadMultiSecteurAccess();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setEchangesPilotEligible(false);
      return;
    }
    let cancelled = false;
    apiFetch("/api/v2/multisecteur-echanges-pilot-autologin-eligible", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        if (!cancelled) setEchangesPilotEligible(Boolean(d.eligible));
      })
      .catch(() => {
        if (!cancelled) setEchangesPilotEligible(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const normName = (s) => String(s || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const candidatePrenoms = [
    user?.prenom,
    user?.medecin_prenom,
    medecinData?.prenom,
  ].map(normName);
  const candidateNoms = [
    user?.nom,
    user?.medecin_nom,
    medecinData?.nom,
  ].map(normName);

  /** Accès tests / cartes spécifiques (thoracique preview, etc.). */
  const isBaillard =
    candidatePrenoms.some((p) => p.includes("christophe")) &&
    candidateNoms.some((n) => n === "baillard");
  const isDrSabriDalel =
    candidateNoms.some((n) => ["dalel", "dallel"].includes(n)) &&
    candidatePrenoms.some((p) => p.includes("sabri"));
  /** Assistante : accès planning OPH multisecteurs calqué sur les mêmes garde-fous que le planning PR (carte dédiée). */
  const isAudreyPrimot =
    candidateNoms.some((n) => n === "primot") &&
    candidatePrenoms.some((p) => p.includes("audrey"));
  const roleNorm = normName(user?.role || "");
  const categorieJunior = String(user?.categorie_junior || "senior").trim().toLowerCase() || "senior";
  const canAccessSeniorPortRoyalGardes =
    categorieJunior !== "interne_junior_pur" && categorieJunior !== "stagiaire_associe";
  const canAccessAgendaByRole = roleNorm === "supervision" || roleNorm === "responsable" || roleNorm === "responsable_thoracique";
  const canAccessAgendaBySecteurResponsable = Array.isArray(multiSecteurAccess) && multiSecteurAccess.length > 0;
  // Comptes médecins : le rôle n'est pas toujours renseigné comme « medecin » dans les comptes importés ;
  // le JWT peut porter medecin_id. En support lecture seule, la cible est toujours un compte « médecin »
  // (hors responsable/supervision) : on affiche l'agenda même si role/medecin_id est incohérent.
  const isMedecinLikeAccount = roleNorm === "medecin" || Boolean(user?.medecin_id);
  const canAccessAgendaMultisecteur =
    isMedecinLikeAccount ||
    canAccessAgendaByRole ||
    canAccessAgendaBySecteurResponsable ||
    Boolean(user?.support_readonly);

  const canAccessPlanningPR = useMemo(() => {
    if (!participatingPlanningPRInterieur) return false;
    if (!cfgPlanningPR) return false; // config pas encore chargée
    if (!cfgPlanningPR.acces_restreint) return true; // pas de restriction
    const autorises = cfgPlanningPR.medecins_autorises || [];
    if (autorises.length === 0) return true;
    if (!user?.nom || !user?.prenom) return false;
    return autorises.some(a => normName(a.nom) === normName(user.nom) && normName(a.prenom) === normName(user.prenom));
  }, [cfgPlanningPR, user, participatingPlanningPRInterieur]);

  const showPlanningPRCard = useMemo(() => {
    const hasOpenMonth = Array.isArray(cfgPlanningPR?.mois_ouverts) && cfgPlanningPR.mois_ouverts.length > 0;
    return hasOpenMonth && canAccessPlanningPR;
  }, [cfgPlanningPR, canAccessPlanningPR]);

  const showPortRoyalCard = canAccessSeniorPortRoyalGardes || showPlanningPRCard;

  /** Même périmètre que la carte « Planning Port-Royal » : liste intérieurs PR + config acces_restreint / medecins_autorises. */
  const showPrimotOphPlanningCard = showPlanningPRCard && isAudreyPrimot;
  const showServiceGardesAstreintesCard = true;

  const canAccessPlanningThoracique = useMemo(() => {
    const hasOpenMonth = Array.isArray(cfgPlanningThoracique?.mois_ouverts) && cfgPlanningThoracique.mois_ouverts.length > 0;
    if (!hasOpenMonth) return false;

    if (roleNorm === "supervision" || roleNorm === "responsable" || roleNorm === "responsable_thoracique") {
      return true;
    }
    if (!participatingAstreinteThoraciqueInterieur) return false;

    const openMonths = Array.isArray(cfgPlanningThoracique?.mois_ouverts) ? cfgPlanningThoracique.mois_ouverts : [];
    const primaryMonth = String(openMonths[0]?.mois_cle || cfgPlanningThoracique?.mois_cle || "").trim();
    const restrictionsByMonth = cfgPlanningThoracique?.restrictions_par_mois || {};

    let accesRestreint = Boolean(cfgPlanningThoracique?.acces_restreint);
    let autorises = Array.isArray(cfgPlanningThoracique?.medecins_autorises) ? cfgPlanningThoracique.medecins_autorises : [];
    if (primaryMonth && restrictionsByMonth && typeof restrictionsByMonth === "object") {
      const monthRule = restrictionsByMonth[primaryMonth];
      if (monthRule && typeof monthRule === "object") {
        if (typeof monthRule.acces_restreint === "boolean") accesRestreint = monthRule.acces_restreint;
        if (Array.isArray(monthRule.medecins_autorises)) autorises = monthRule.medecins_autorises;
      }
    }
    if (!accesRestreint) return true;
    if (autorises.length === 0) return true;

    const currentNom = normName(medecinData?.nom || user?.nom || "");
    const currentPrenom = normName(medecinData?.prenom || user?.prenom || "");
    if (!currentNom || !currentPrenom) return false;
    return autorises.some((a) => normName(a.nom || "") === currentNom && normName(a.prenom || "") === currentPrenom);
  }, [cfgPlanningThoracique, user, medecinData, participatingAstreinteThoraciqueInterieur, roleNorm]);

  const showPlanningThoraciqueCard = useMemo(() => {
    const hasOpenMonth = Array.isArray(cfgPlanningThoracique?.mois_ouverts) && cfgPlanningThoracique.mois_ouverts.length > 0;
    return hasOpenMonth && participatingAstreinteThoraciqueInterieur;
  }, [cfgPlanningThoracique, participatingAstreinteThoraciqueInterieur]);

  // Charger les données du médecin et vérifier sa participation
  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        // Evite toute fuite visuelle d'etat lors d'un switch de session support lecture seule
        setParticipatingGardes(false);
        setParticipatingGardesPr1(false);
        setParticipatingPlanningPRInterieur(false);
        setParticipatingGardesReanimation(false);
        setParticipatingAstreinteThoracique(false);
        setParticipatingAstreinteThoraciqueInterieur(false);
        setParticipatingGardesDigestif(false);
        setParticipatingPlanningDigestifInterieur(false);

        // Récupérer les données du médecin depuis la liste maître
        let medecin = null;
        if (user.medecin_id) {
          const medecins = await fetchMedecinsMaitre();
          medecin = medecins.find((m) => m.medecin_id === user.medecin_id);
          if (medecin) {
            setMedecinData(medecin);
            setMedecinName(`${medecin.prenom} ${medecin.nom}`);
          } else if (user.nom && user.prenom) {
            setMedecinName(`${user.prenom} ${user.nom}`);
          }
        } else if (user.nom && user.prenom) {
          setMedecinName(`${user.prenom} ${user.nom}`);
        }

        // Vérifier la participation aux activités
        if (medecin || (user.nom && user.prenom)) {
          const nom = medecin?.nom || user.nom;
          const prenom = medecin?.prenom || user.prenom;


          // Vérifier participation Gardes PR2
          const resGardes = await apiFetch("/api/v2/medecins-gardes-pr2");
          if (resGardes.ok) {
            const medecinsGardes = await resGardes.json();
            const isInGardes = medecinsGardes.some(
              (m) =>
                String(m.nom || "").toLowerCase() === String(nom || "").toLowerCase() &&
                String(m.prenom || "").toLowerCase() === String(prenom || "").toLowerCase()
            );
            setParticipatingGardes(isInGardes);
          }

          // Vérifier participation Gardes PR1
          const resGardesPr1 = await apiFetch("/api/v2/medecins-gardes-pr1");
          if (resGardesPr1.ok) {
            const listPr1 = await resGardesPr1.json();
            const isInPr1 = listPr1.some(
              (m) =>
                String(m.nom || "").toLowerCase() === String(nom || "").toLowerCase() &&
                String(m.prenom || "").toLowerCase() === String(prenom || "").toLowerCase()
            );
            setParticipatingGardesPr1(isInPr1);
          }

          // Vérifier accès planning Port-Royal (internes uniquement)
          const resPlanningPr = await apiFetch(PLANNING_PR_MEDECINS_ENDPOINT, { credentials: "include" });
          if (resPlanningPr.ok) {
            const listPlanningPr = await resPlanningPr.json();
            const isInPlanningPrInterieur = listPlanningPr.some(
              (m) =>
                String(m.nom || "").toLowerCase() === String(nom || "").toLowerCase() &&
                String(m.prenom || "").toLowerCase() === String(prenom || "").toLowerCase()
            );
            setParticipatingPlanningPRInterieur(isInPlanningPrInterieur);
          }

          // Vérifier participation Gardes Réanimation
          const resGardesReanimation = await apiFetch("/api/v2/medecins-gardes-reanimation");
          if (resGardesReanimation.ok) {
            const listRea = await resGardesReanimation.json();
            const isInRea = listRea.some(
              (m) =>
                String(m.nom || "").toLowerCase() === String(nom || "").toLowerCase() &&
                String(m.prenom || "").toLowerCase() === String(prenom || "").toLowerCase()
            );
            setParticipatingGardesReanimation(isInRea);
          }

          // Vérifier participation Astreinte thoracique
          const resAstreinte = await apiFetch("/api/v2/medecins-astreintes-thoracique");
          if (resAstreinte.ok) {
            const listAstreinte = await resAstreinte.json();
            const meInAstreinte =
              listAstreinte.find(
                (m) =>
                  String(m.nom || "").toLowerCase() === String(nom || "").toLowerCase() &&
                  String(m.prenom || "").toLowerCase() === String(prenom || "").toLowerCase()
              ) || null;
            const isInAstreinte = !!meInAstreinte;
            const isInAstreinteInterieur =
              !!meInAstreinte &&
              String(meInAstreinte.origine || "interieur").toLowerCase().trim() === "interieur";
            setParticipatingAstreinteThoracique(isInAstreinte);
            setParticipatingAstreinteThoraciqueInterieur(isInAstreinteInterieur);
          }

          // Vérifier participation Garde Digestif
          const resDigestif = await apiFetch("/api/v2/medecins-gardes-digestif");
          if (resDigestif.ok) {
            const listDigestif = await resDigestif.json();
            const meInDigestif =
              listDigestif.find(
                (m) =>
                  String(m.nom || "").toLowerCase() === String(nom || "").toLowerCase() &&
                  String(m.prenom || "").toLowerCase() === String(prenom || "").toLowerCase()
              ) || null;
            const isInDigestif = !!meInDigestif;
            const isInDigestifInterieur =
              !!meInDigestif &&
              String(meInDigestif.origine || "interieur").toLowerCase().trim() === "interieur";
            setParticipatingGardesDigestif(isInDigestif);
            setParticipatingPlanningDigestifInterieur(isInDigestifInterieur);
          }
        }
      } catch (err) {
        console.error("❌ Erreur chargement données :", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  const S = {
    container: {
      minHeight: "100dvh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background:
        "linear-gradient(135deg, #eef2ff 0%, #f9fafb 40%, #ecfeff 100%)",
      fontFamily:
        "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      padding: "clamp(22px, 3.5dvh, 42px) clamp(24px, 4vw, 64px)",
    },
    card: {
      width: "min(calc(100vw - 72px), 1180px)",
      maxWidth: "1180px",
      background: "#ffffff",
      borderRadius: "20px",
      padding: "clamp(30px, 3vw, 44px)",
      boxShadow: "0 28px 72px rgba(15,23,42,0.18)",
      border: "1px solid #e5e7eb",
    },
    title: {
      fontSize: "clamp(28px, 2.15vw, 38px)",
      fontWeight: 800,
      marginBottom: "10px",
      color: "#111827",
      lineHeight: 1.08,
    },
    subtitle: {
      fontSize: "clamp(15px, 1.1vw, 18px)",
      color: "#4b5563",
      marginBottom: "18px",
      lineHeight: 1.5,
      maxWidth: "1040px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))",
      gap: "18px",
      marginTop: "12px",
    },
    btn: {
      padding: "20px 20px",
      borderRadius: "14px",
      border: "1px solid #111827",
      background: "#111827",
      color: "#ffffff",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: 800,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "8px",
      boxShadow: "0 10px 30px rgba(15,23,42,0.22)",
      transition: "transform 0.12s ease, box-shadow 0.12s ease",
    },
    btnLight: {
      background: "#ffffff",
      color: "#111827",
      border: "1px solid #d1d5db",
      boxShadow: "0 8px 22px rgba(148,163,184,0.35)",
    },
    btnTitle: {
      fontSize: "clamp(18px, 1.35vw, 22px)",
      fontWeight: 900,
    },
    btnDesc: {
      fontSize: "14px",
      fontWeight: 600,
      opacity: 0.9,
      lineHeight: 1.45,
    },
    footer: {
      marginTop: "18px",
      fontSize: "13px",
      color: "#6b7280",
    },
    activityCard: {
      padding: "20px 20px",
      borderRadius: "14px",
      border: "1px solid #111827",
      background: "#111827",
      color: "#ffffff",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: 800,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "10px",
      boxShadow: "0 10px 30px rgba(15,23,42,0.22)",
      transition: "transform 0.12s ease, box-shadow 0.12s ease",
      position: "relative",
    },
    checkboxContainer: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginTop: "4px",
    },
    checkbox: {
      width: "20px",
      height: "20px",
      cursor: "pointer",
      accentColor: "#f59e0b",
    },
    checkboxLabel: {
      fontSize: "14px",
      fontWeight: 600,
      opacity: 0.95,
    },
  };

  // Fonction pour gérer le toggle de participation
  const handleToggleParticipation = async (activity, currentValue) => {
    console.log("🔍 [PortailMedecin] handleToggleParticipation appelé", { activity, currentValue, medecinData: !!medecinData, user: user?.username });
    
    if (!medecinData && !(user.nom && user.prenom)) {
      notify("Impossible de modifier la participation : données médecin manquantes.");
      return;
    }

    const nom = medecinData?.nom || user.nom;
    const prenom = medecinData?.prenom || user.prenom;
    const medecinToAdd = medecinData || {
      nom,
      prenom,
      statut: "Praticien hospitalier",
      quotite: 100,
      activites: {},
      medecin_id: user.medecin_id || null,
    };

    // Si on décoche, afficher le pop-up de confirmation
    if (currentValue) {
      console.log("✅ [PortailMedecin] Affichage du modal de confirmation pour", activity);
      setShowExclusionModal({ open: true, activity });
      return;
    }

    // Si on coche, ajouter directement
    console.log("✅ [PortailMedecin] Ajout de la participation pour", activity);
    await updateParticipation(activity, true, medecinToAdd);
  };

  // Fonction pour confirmer l'exclusion
  const handleConfirmExclusion = async () => {
    const activity = showExclusionModal.activity; // Sauvegarder avant de fermer le modal
    console.log("🔍 [PortailMedecin] handleConfirmExclusion appelé", { activity, medecinData: !!medecinData, user: user?.username });
    
    if (!medecinData && !(user.nom && user.prenom)) {
      console.warn("⚠️ [PortailMedecin] Données médecin manquantes pour exclusion");
      setShowExclusionModal({ open: false, activity: null });
      return;
    }

    const nom = medecinData?.nom || user.nom;
    const prenom = medecinData?.prenom || user.prenom;

    console.log("✅ [PortailMedecin] Appel updateParticipation pour exclusion", { nom, prenom, activity });
    
    // Fermer le modal d'abord pour une meilleure UX
    setShowExclusionModal({ open: false, activity: null });
    
    // Mettre à jour la participation avec l'activité sauvegardée
    await updateParticipation(activity, false, { nom, prenom });
  };

  // Endpoints et libellés par activité (pour participation médecin)
  const ACTIVITY_CONFIG = {
    gardes: { endpoint: "/api/v2/medecins-gardes-pr2/participation", label: "Garde Port Royal 2" },
    "gardes-pr1": { endpoint: "/api/v2/medecins-gardes-pr1/participation", label: "Garde Port Royal 1" },
    "gardes-reanimation": { endpoint: "/api/v2/medecins-gardes-reanimation/participation", label: "Garde Réanimation" },
    "astreinte-thoracique": { endpoint: "/api/v2/medecins-astreintes-thoracique/participation", label: "Astreinte thoracique" },
    "gardes-digestif": { endpoint: "/api/v2/medecins-gardes-digestif/participation", label: "Garde Digestif" },
  };

  const updateParticipation = async (activity, add, medecinInfo) => {
    const config = ACTIVITY_CONFIG[activity];
    if (!config) {
      notify("Activité inconnue.");
      return;
    }
    try {
      setUpdating(true);
      const body = add ? { add: true, medecin: medecinInfo } : { add: false };
      const res = await apiFetch(config.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(`❌ [PortailMedecin] Erreur ${config.endpoint}: ${res.status}`, errorText);
        throw new Error(`Erreur HTTP ${res.status}`);
      }

      const result = await res.json();
      console.log(`✅ [PortailMedecin] Participation ${activity} = ${add}`, result);

      if (activity === "gardes") setParticipatingGardes(add);
      else if (activity === "gardes-pr1") setParticipatingGardesPr1(add);
      else if (activity === "gardes-reanimation") setParticipatingGardesReanimation(add);
      else if (activity === "astreinte-thoracique") setParticipatingAstreinteThoracique(add);
      else if (activity === "gardes-digestif") setParticipatingGardesDigestif(add);
    } catch (err) {
      console.error(`❌ Erreur mise à jour participation ${activity}:`, err);
      notify(`Erreur lors de la mise à jour de votre participation à ${config.label}.`);
    } finally {
      setUpdating(false);
    }
  };

  // Debug: afficher l'état de updating et loading
  useEffect(() => {
    console.log("🔍 [PortailMedecin] État:", { updating, loading, user: user?.username });
  }, [updating, loading, user]);

  // Ouvre soit un nouvel onglet (mode responsable), soit la page courante (agenda médecin),
  // puis finalise l'auto-login dès que le backend renvoie le token.
  const openMultiSecteurWithAutologin = async ({ secteur = "", targetPath, openInNewTab = true }) => {
    let navWindow = window;
    if (openInNewTab) {
      const popup = window.open("", "_blank");
      if (!popup) {
        notify("Le navigateur a bloque l'ouverture de l'onglet. Autorisez les pop-ups pour ce site.");
        return;
      }
      popup.document.write("<p style='font-family:sans-serif;padding:24px'>Connexion en cours...</p>");
      navWindow = popup;
    }

    try {
      const query = String(secteur || "").trim() ? `?secteur=${encodeURIComponent(String(secteur).trim())}` : "";
      const res = await apiFetch(`/api/v2/multisecteur-autologin${query}`, {
        credentials: "include",
      });
      const rawBody = await res.text().catch(() => "");
      if (!res.ok) {
        let detail = rawBody;
        try {
          const j = JSON.parse(rawBody);
          detail = j.detail || j.error || j.message || rawBody;
        } catch {
          /* ignore */
        }
        console.error("❌ multisecteur-autologin HTTP", res.status, detail);
        throw new Error(String(detail || `Erreur ${res.status}`).slice(0, 240));
      }
      const data = JSON.parse(rawBody);

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("user", JSON.stringify(data.user));

      navWindow.location.href = targetPath;
    } catch (err) {
      console.error("❌ Erreur auto-login multi-secteurs:", err);
      const q = new URLSearchParams({ from: "port-royal-autologin", reason: "failed" });
      const hint = String(err?.message || "").trim();
      if (hint) q.set("hint", hint.slice(0, 240));
      navWindow.location.href = `/multisecteur/index.html?${q.toString()}`;
    }
  };

  const openMultiSecteurMedecinEchangesAutologin = async ({ targetPath, openInNewTab = false }) => {
    let navWindow = window;
    if (openInNewTab) {
      const popup = window.open("", "_blank");
      if (!popup) {
        notify("Le navigateur a bloque l'ouverture de l'onglet. Autorisez les pop-ups pour ce site.");
        return;
      }
      popup.document.write("<p style='font-family:sans-serif;padding:24px'>Connexion au planning multi-secteurs en cours...</p>");
      navWindow = popup;
    }
    try {
      const res = await apiFetch("/api/v2/multisecteur-medecin-pilot-autologin", {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`Erreur ${res.status}`);
      }
      const data = await res.json();
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("user", JSON.stringify(data.user));
      navWindow.location.href = targetPath;
    } catch (err) {
      console.error("❌ Erreur auto-login échanges garde/astreinte:", err);
      notify("Ouverture du module impossible : vérifiez que votre compte dispose d'une fiche médecin liée au planning multi-secteurs.");
    }
  };

  const exitReadOnlySupport = async () => {
    try {
      setRestoringSupportSession(true);
      const res = await apiFetch("/api/v2/auth/support/read-only-restore", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || "Restauration de session impossible.");
      }
      window.location.href = "/auth";
    } catch (err) {
      notify(`Erreur restauration session: ${err.message}`);
    } finally {
      setRestoringSupportSession(false);
    }
  };

  const openDigestifResponsableDirect = async () => {
    try {
      setOpeningDigestifResponsable(true);
      sessionStorage.setItem("scoped_responsable_digestif", "1");
      const res = await apiFetch("/api/v2/auth/trusted-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ secteur: "sect-digestif" }),
      });
      if (!res.ok) {
        throw new Error(`Erreur ${res.status}`);
      }
      const issued = await res.json();
      if (!issued.token || !issued.user) throw new Error('Session responsable indisponible');
      const isolated = issued.isolated || sessionStorage.getItem('auth_prefer_session_v1') === '1';
      if (isolated) sessionStorage.setItem('auth_prefer_session_v1', '1');
      for (const storage of isolated ? [sessionStorage] : [localStorage, sessionStorage]) {
        storage.setItem('token', issued.token);
        storage.setItem('user', JSON.stringify(issued.user));
      }
      window.location.href = "/responsable/gardes?type=DIGESTIF&lockSecteur=1&back=%2Fportail-medecin";
    } catch (err) {
      console.error("❌ Erreur ouverture interface responsable digestif:", err);
      notify("Impossible d'ouvrir l'interface responsable Digestif pour le moment.");
    } finally {
      setOpeningDigestifResponsable(false);
    }
  };

  if (new URLSearchParams(window.location.search).get("vue") === "bourse") {
    return <div style={S.container}><BourseTravailMedecin /></div>;
  }

  return (
    <div style={S.container}>
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
          <div style={{ flex: 1 }}>
            <h1 style={S.title}>
              Espace médecins<br />
              <span style={{ fontSize: "clamp(20px, 1.45vw, 26px)", fontWeight: 600 }}>Anesthésie Cochin-Port Royal</span>
            </h1>
            {medecinName && (
              <div style={{
                fontSize: "clamp(20px, 1.45vw, 26px)",
                fontWeight: 700,
                color: "#111827",
                marginTop: "10px",
                marginBottom: "6px",
                padding: "10px 16px",
                background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                borderRadius: "12px",
                border: "2px solid #f59e0b",
                display: "inline-block",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.2)",
              }}>
                👤 {medecinName}
              </div>
            )}
            <p style={S.subtitle}>
              Vous êtes connecté. Choisissez l'activité que vous souhaitez consulter ou
              renseigner.
            </p>
            {Boolean(user?.support_readonly) && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid #f59e0b",
                  background: "#fff7ed",
                  color: "#9a3412",
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                🛟 Mode support lecture seule actif
                <button
                  type="button"
                  onClick={exitReadOnlySupport}
                  disabled={restoringSupportSession}
                  style={{
                    border: "1px solid #c2410c",
                    background: "#fff",
                    color: "#9a3412",
                    borderRadius: "8px",
                    padding: "6px 10px",
                    fontWeight: 700,
                    cursor: restoringSupportSession ? "not-allowed" : "pointer",
                    opacity: restoringSupportSession ? 0.7 : 1,
                  }}
                >
                  {restoringSupportSession ? "Restauration..." : "Quitter le mode support"}
                </button>
              </div>
            )}
            {/* Debug: afficher l'état */}
            {(updating || loading) && (
              <div style={{ fontSize: "12px", color: "#dc2626", marginTop: "8px" }}>
                ⚠️ État: updating={String(updating)}, loading={String(loading)}
              </div>
            )}
          </div>
          <LogoutButton style={{ marginLeft: "16px" }} />
        </div>

        {bourseEligible && <div style={{marginBottom:18}}>
          <a href="/portail-medecin?vue=bourse" style={{display:"block",padding:14,background:"#0f766e",color:"white",borderRadius:6,textAlign:"center",fontWeight:700}}>Bourse du travail</a>
        </div>}

        {canAccessAgendaMultisecteur && (
        <div style={{ marginBottom: "18px" }}>
          <button
            type="button"
            onClick={() => {
              const targetNom = (medecinData?.nom || user?.nom || "").trim();
              const targetPrenom = (medecinData?.prenom || user?.prenom || "").trim();
              const qp = [];
              if (targetNom) qp.push(`medecin_nom=${encodeURIComponent(targetNom)}`);
              if (targetPrenom) qp.push(`medecin_prenom=${encodeURIComponent(targetPrenom)}`);
              const query = qp.length ? `?${qp.join("&")}` : "";
              openMultiSecteurWithAutologin({
                secteur: multiSecteurAccess?.[0]?.secteur_id || "",
                targetPath: `/multisecteur/medecin/agenda.html${query}`,
                openInNewTab: false,
              });
            }}
            style={{
              width: "100%",
              padding: "13px 18px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
              color: "#ffffff",
              fontSize: "15px",
              fontWeight: 800,
              border: "none",
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(15,23,42,0.35)",
              transition: "transform 0.12s ease, box-shadow 0.12s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              textDecoration: "none",
              boxSizing: "border-box",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 12px 32px rgba(15,23,42,0.45)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(15,23,42,0.35)";
            }}
          >
            🗓️ Mon agenda multisecteurs
          </button>
        </div>
        )}

        {showServiceGardesAstreintesCard && (
        <div style={{ marginBottom: "18px" }}>
          <button
            type="button"
            onClick={() => {
              openMultiSecteurWithAutologin({
                targetPath: "/multisecteur/medecin/gardes-astreintes-service.html",
                openInNewTab: false,
              });
            }}
            style={{
              width: "100%",
              padding: "13px 18px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #14532d 0%, #0f766e 100%)",
              color: "#ffffff",
              fontSize: "15px",
              fontWeight: 800,
              border: "none",
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(15,118,110,0.35)",
              transition: "transform 0.12s ease, box-shadow 0.12s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              textDecoration: "none",
              boxSizing: "border-box",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 12px 32px rgba(15,118,110,0.45)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(15,118,110,0.35)";
            }}
          >
            📊 Gardes et astreintes du service
          </button>
        </div>
        )}

        {echangesPilotEligible && (
        <div style={{ marginBottom: "18px" }}>
          <button
            type="button"
            onClick={() => {
              openMultiSecteurMedecinEchangesAutologin({
                targetPath: "/multisecteur/medecin/echanges-gardes.html",
                openInNewTab: false,
              });
            }}
            style={{
              width: "100%",
              padding: "13px 18px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
              color: "#ffffff",
              fontSize: "15px",
              fontWeight: 800,
              border: "none",
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(79,70,229,0.35)",
              transition: "transform 0.12s ease, box-shadow 0.12s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              textDecoration: "none",
              boxSizing: "border-box",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 12px 32px rgba(79,70,229,0.45)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(79,70,229,0.35)";
            }}
            title="Mode pilote : accès médecin filtré côté serveur (liste blanche) jusqu'à bascule ECHANGES_GARDES_MEDECINS_MODE=all"
          >
            🔄 Échanges garde/astreinte (pilote)
          </button>
        </div>
        )}

        {isDrSabriDalel && (
          <div style={{ marginBottom: "18px" }}>
            <button
              type="button"
              onClick={openDigestifResponsableDirect}
              disabled={openingDigestifResponsable}
              style={{
                width: "100%",
                padding: "13px 18px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
                color: "#ffffff",
                fontSize: "15px",
                fontWeight: 800,
                border: "none",
                cursor: "pointer",
                boxShadow: "0 8px 24px rgba(29,78,216,0.35)",
                transition: "transform 0.12s ease, box-shadow 0.12s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                boxSizing: "border-box",
                opacity: openingDigestifResponsable ? 0.75 : 1,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 12px 32px rgba(29,78,216,0.45)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(29,78,216,0.35)";
              }}
              title="Accès direct à l'interface responsable Digestif"
            >
              <span>{openingDigestifResponsable ? "⏳ Ouverture interface responsable..." : "👨‍💼 Interface responsable — Digestif"}</span>
              <span style={{ fontSize: "13px", fontWeight: 700, opacity: 0.9 }}>secteur dédié</span>
            </button>
          </div>
        )}

        {!!multiSecteurAccess.length && (
          <div style={{ marginBottom: "18px", display: "grid", gap: "10px" }}>
            {multiSecteurAccess.map((item) => (
              <button
                key={item.secteur_id}
                type="button"
                onClick={() => {
                  openMultiSecteurWithAutologin({
                    secteur: item.secteur_id,
                    targetPath: item.targetPath || `/multisecteur/responsable/index.html?secteur=${encodeURIComponent(item.secteur_id)}&lockSecteur=1`,
                  });
                }}
                style={{
                  width: "100%",
                  padding: "12px 18px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
                  color: "#ffffff",
                  fontSize: "15px",
                  fontWeight: 800,
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 8px 24px rgba(13,148,136,0.35)",
                  transition: "transform 0.12s ease, box-shadow 0.12s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  textDecoration: "none",
                  boxSizing: "border-box",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 12px 32px rgba(13,148,136,0.45)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(13,148,136,0.35)";
                }}
                title="Accès responsable multi-secteurs restreint à votre secteur"
              >
                <span>📋 Gestion multi-secteurs — {item.secteur_nom || item.secteur_id}</span>
                <span style={{ fontSize: "13px", fontWeight: 700, opacity: 0.9 }}>secteur dédié</span>
              </button>
            ))}
          </div>
        )}
        {loadingMultiSecteurAccess && (
          <div style={{ marginBottom: "18px", color: "#64748b", fontSize: "13px", fontWeight: 600 }}>
            Vérification des accès multisecteurs responsables...
          </div>
        )}

        {/* Vue sous-page Port Royal */}
        {portRoyalOpen ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
            {/* Bouton retour */}
            <button
              onClick={() => setPortRoyalOpen(false)}
              style={{
                alignSelf: "flex-start",
                padding: "10px 20px",
                borderRadius: "10px",
                border: "1px solid #334155",
                background: "#1e293b",
                color: "#e2e8f0",
                fontSize: "15px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background 0.15s",
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = "#334155"; }}
              onMouseOut={(e) => { e.currentTarget.style.background = "#1e293b"; }}
            >
              ← Retour
            </button>

            <h2 style={{ color: "#f8fafc", fontSize: "22px", fontWeight: 700, margin: "0 0 4px 0" }}>
              Port Royal
            </h2>

            <div style={S.grid}>
              {canAccessSeniorPortRoyalGardes && (
              <>
              {/* Carte Garde PR2 */}
              <div
                style={S.activityCard}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 14px 38px rgba(15,23,42,0.30)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 30px rgba(15,23,42,0.22)";
                }}
              >
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <span style={S.btnTitle}>🩺 Garde Port Royal 2</span>
                    <span style={S.btnDesc}>Désidératas de gardes et consultation des listes validées.</span>
                  </div>
                  <Link
                    to="/interface-medecins"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      padding: "8px 16px", borderRadius: "8px", border: "1px solid #ffffff",
                      background: "#ffffff", color: "#111827", fontSize: "14px", fontWeight: 600,
                      cursor: "pointer", textDecoration: "none", display: "inline-block",
                      position: "relative", zIndex: 100,
                    }}
                  >
                    Accéder
                  </Link>
                </div>
                <div style={S.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={participatingGardes}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (updating || loading) { e.preventDefault(); return; }
                      if (participatingGardes) {
                        e.preventDefault();
                        handleToggleParticipation("gardes", true);
                      } else {
                        handleToggleParticipation("gardes", false);
                      }
                    }}
                    onChange={() => {}}
                    disabled={updating || loading}
                    style={S.checkbox}
                    id="checkbox-gardes"
                  />
                  <label htmlFor="checkbox-gardes" style={S.checkboxLabel}>
                    Je participe à cette activité {participatingGardes ? "✓" : "✗"}
                  </label>
                </div>
              </div>

              {/* Carte Garde PR1 */}
              <div
                style={S.activityCard}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 14px 38px rgba(15,23,42,0.30)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 30px rgba(15,23,42,0.22)";
                }}
              >
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <span style={S.btnTitle}>🩺 Garde Port Royal 1</span>
                    <span style={S.btnDesc}>Désidératas de gardes et consultation des listes validées.</span>
                  </div>
                  <Link
                    to="/interface-medecins-pr1"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      padding: "8px 16px", borderRadius: "8px", border: "1px solid #ffffff",
                      background: "#ffffff", color: "#111827", fontSize: "14px", fontWeight: 600,
                      cursor: "pointer", textDecoration: "none", display: "inline-block",
                      position: "relative", zIndex: 100,
                    }}
                  >
                    Accéder
                  </Link>
                </div>
                <div style={S.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={participatingGardesPr1}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (updating || loading) { e.preventDefault(); return; }
                      if (participatingGardesPr1) {
                        e.preventDefault();
                        handleToggleParticipation("gardes-pr1", true);
                      } else {
                        handleToggleParticipation("gardes-pr1", false);
                      }
                    }}
                    onChange={() => {}}
                    disabled={updating || loading}
                    style={S.checkbox}
                    id="checkbox-gardes-pr1"
                  />
                  <label htmlFor="checkbox-gardes-pr1" style={S.checkboxLabel}>
                    Je participe à cette activité {participatingGardesPr1 ? "✓" : "✗"}
                  </label>
                </div>
              </div>
              </>
              )}

              {/* Carte Planning Port-Royal (visible uniquement si autorisé) */}
              {showPlanningPRCard && (
              <div
                style={S.activityCard}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 14px 38px rgba(15,23,42,0.30)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 30px rgba(15,23,42,0.22)";
                }}
              >
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <span style={S.btnTitle}>📋 Planning Port-Royal</span>
                    <span style={S.btnDesc}>Désidératas de disponibilité pour le planning Port-Royal.</span>
                  </div>
                  <Link
                    to="/desiderata-planning-port-royal"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canAccessPlanningPR) {
                        e.preventDefault();
                        notify("Vous n'avez pas accès à ce planning.");
                      }
                    }}
                    style={{
                      padding: "8px 16px", borderRadius: "8px", border: "1px solid #ffffff",
                      background: "#ffffff", color: "#111827", fontSize: "14px", fontWeight: 600,
                      cursor: "pointer", textDecoration: "none", display: "inline-block",
                      position: "relative", zIndex: 100,
                    }}
                  >
                    Accéder
                  </Link>
                </div>
              </div>
              )}

              {showPrimotOphPlanningCard && (
              <div
                style={S.activityCard}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 14px 38px rgba(15,23,42,0.30)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 30px rgba(15,23,42,0.22)";
                }}
              >
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <span style={S.btnTitle}>👁️ Planning Ophtalmologie</span>
                    <span style={S.btnDesc}>
                      Édition du planning OPH (interface multisecteurs, nouvel onglet). Accès réservé comme pour le planning Port-Royal, sans supervision générale.
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canAccessPlanningPR) {
                        notify("Vous n'avez pas accès à ce planning.");
                        return;
                      }
                      openMultiSecteurWithAutologin({
                        secteur: "oph",
                        targetPath:
                          "/multisecteur/responsable/planning-oph-edition.html?secteur=sect-oph",
                        openInNewTab: true,
                      });
                    }}
                    style={{
                      padding: "8px 16px", borderRadius: "8px", border: "1px solid #ffffff",
                      background: "#ffffff", color: "#111827", fontSize: "14px", fontWeight: 600,
                      cursor: "pointer", textDecoration: "none", display: "inline-block",
                      position: "relative", zIndex: 100,
                    }}
                  >
                    Ouvrir
                  </button>
                </div>
              </div>
              )}
            </div>
          </div>
        ) : thoraciqueOpen ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
            <button
              onClick={() => setThoraciqueOpen(false)}
              style={{
                alignSelf: "flex-start",
                padding: "10px 20px",
                borderRadius: "10px",
                border: "1px solid #334155",
                background: "#1e293b",
                color: "#e2e8f0",
                fontSize: "15px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background 0.15s",
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = "#334155"; }}
              onMouseOut={(e) => { e.currentTarget.style.background = "#1e293b"; }}
            >
              ← Retour
            </button>

            <h2 style={{ color: "#f8fafc", fontSize: "22px", fontWeight: 700, margin: "0 0 4px 0" }}>
              Thoracique
            </h2>

            <div style={S.grid}>
              <div
                style={S.activityCard}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 14px 38px rgba(15,23,42,0.30)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 10px 30px rgba(15,23,42,0.22)";
                }}
              >
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <span style={S.btnTitle}>🫁 Astreinte thoracique</span>
                    <span style={S.btnDesc}>
                      Désidératas d&apos;astreinte et consultation des listes validées.
                    </span>
                  </div>
                  <Link
                    to="/interface-astreinte-thoracique"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "8px",
                      border: "1px solid #ffffff",
                      background: "#ffffff",
                      color: "#111827",
                      fontSize: "14px",
                      fontWeight: 600,
                      cursor: "pointer",
                      textDecoration: "none",
                      display: "inline-block",
                      position: "relative",
                      zIndex: 100,
                    }}
                  >
                    Accéder
                  </Link>
                </div>
                <div style={S.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={participatingAstreinteThoracique}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (updating || loading) {
                        e.preventDefault();
                        return;
                      }
                      if (participatingAstreinteThoracique) {
                        e.preventDefault();
                        handleToggleParticipation("astreinte-thoracique", true);
                      } else {
                        handleToggleParticipation("astreinte-thoracique", false);
                      }
                    }}
                    onChange={() => {}}
                    disabled={updating || loading}
                    style={S.checkbox}
                    id="checkbox-astreinte-thoracique"
                  />
                  <label htmlFor="checkbox-astreinte-thoracique" style={S.checkboxLabel}>
                    Je participe à cette activité {participatingAstreinteThoracique ? "✓" : "✗"}
                  </label>
                </div>
              </div>

              {showPlanningThoraciqueCard && (
                <div
                  style={S.activityCard}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 14px 38px rgba(15,23,42,0.30)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 10px 30px rgba(15,23,42,0.22)";
                  }}
                >
                  <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <span style={S.btnTitle}>📋 Planning thoracique</span>
                      <span style={S.btnDesc}>
                        Desideratas de planning thoracique (saisie simplifiee) pour alimentation multisecteur.
                      </span>
                    </div>
                    <Link
                      to="/desiderata-planning-thoracique"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canAccessPlanningThoracique) {
                          e.preventDefault();
                          notify("Vous n'avez pas accès à ce planning.");
                        }
                      }}
                      style={{
                        padding: "8px 16px",
                        borderRadius: "8px",
                        border: "1px solid #ffffff",
                        background: "#ffffff",
                        color: "#111827",
                        fontSize: "14px",
                        fontWeight: 600,
                        cursor: "pointer",
                        textDecoration: "none",
                        display: "inline-block",
                        position: "relative",
                        zIndex: 100,
                      }}
                    >
                      Acceder
                    </Link>
                  </div>
                </div>
              )}

            </div>
          </div>
        ) : (
        <div style={S.grid}>

          {/* Carte Port Royal */}
          {showPortRoyalCard && (
          <div
            style={{ ...S.activityCard, cursor: "pointer" }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 14px 38px rgba(15,23,42,0.30)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 10px 30px rgba(15,23,42,0.22)";
            }}
            onClick={() => setPortRoyalOpen(true)}
          >
            <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <span style={S.btnTitle}>🏥 Port Royal</span>
                <span style={S.btnDesc}>
                  Gardes PR1, Gardes PR2 et désidératas du planning Port-Royal.
                </span>
              </div>
              <span style={{ fontSize: "22px", color: "#94a3b8" }}>→</span>
            </div>
          </div>
          )}

          {/* Carte Thoracique */}
          <div
            style={{ ...S.activityCard, cursor: "pointer" }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 14px 38px rgba(15,23,42,0.30)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 10px 30px rgba(15,23,42,0.22)";
            }}
            onClick={() => setThoraciqueOpen(true)}
          >
            <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <span style={S.btnTitle}>🫁 Thoracique</span>
                <span style={S.btnDesc}>
                  Astreinte thoracique et desideratas du planning thoracique.
                </span>
              </div>
              <span style={{ fontSize: "22px", color: "#94a3b8" }}>→</span>
            </div>
          </div>

          {/* Carte Garde Digestif */}
          {participatingPlanningDigestifInterieur && (
          <div
            style={S.activityCard}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 14px 38px rgba(15,23,42,0.30)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 10px 30px rgba(15,23,42,0.22)";
            }}
            onClick={(e) => {
              const target = e.target;
              if (target === e.currentTarget ||
                  (target.closest("button") === null &&
                   target.closest('input[type="checkbox"]') === null &&
                   target.closest("label") === null &&
                   target.closest("a") === null)) {
                return;
              }
            }}
          >
            <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <span style={S.btnTitle}>🏥 Garde Digestif</span>
                <span style={S.btnDesc}>
                  Désidératas de garde viscérale (Digestif) et consultation des listes validées.
                </span>
              </div>
              {updating ? (
                <div
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "1px solid #ffffff",
                    background: "#ffffff",
                    color: "#111827",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "not-allowed",
                    opacity: 0.6,
                    position: "relative",
                    zIndex: 100,
                  }}
                >
                  Accéder (mise à jour...)
                </div>
              ) : (
                <Link
                  to="/desiderata-planning-digestif"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "1px solid #ffffff",
                    background: "#ffffff",
                    color: "#111827",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "none",
                    display: "inline-block",
                    position: "relative",
                    zIndex: 100,
                  }}
                >
                  Accéder
                </Link>
              )}
            </div>
            <div style={S.checkboxContainer}>
              <input
                type="checkbox"
                checked={participatingGardesDigestif}
                onClick={(e) => {
                  e.stopPropagation();
                  if (updating || loading) {
                    e.preventDefault();
                    return;
                  }
                  if (participatingGardesDigestif) {
                    e.preventDefault();
                    handleToggleParticipation("gardes-digestif", true);
                  } else {
                    handleToggleParticipation("gardes-digestif", false);
                  }
                }}
                onChange={() => {}}
                disabled={updating || loading}
                style={S.checkbox}
                id="checkbox-gardes-digestif"
              />
              <label htmlFor="checkbox-gardes-digestif" style={S.checkboxLabel}>
                Je participe à cette activité {participatingGardesDigestif ? "✓" : "✗"}
              </label>
            </div>
          </div>
          )}

          {/* Carte Garde Réanimation (indépendante de Port Royal) */}
          {isBaillard && (
          <div
            style={S.activityCard}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 14px 38px rgba(15,23,42,0.30)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 10px 30px rgba(15,23,42,0.22)";
            }}
          >
            <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <span style={S.btnTitle}>🏥 Garde Réanimation</span>
                <span style={S.btnDesc}>
                  Désidératas de garde senior en réanimation et consultation des listes validées.
                </span>
              </div>
              {updating ? (
                <div
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "1px solid #ffffff",
                    background: "#ffffff",
                    color: "#111827",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "not-allowed",
                    opacity: 0.6,
                    position: "relative",
                    zIndex: 100,
                  }}
                >
                  Accéder (mise à jour...)
                </div>
              ) : (
                <Link
                  to="/desiderata-garde-reanimation"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "1px solid #ffffff",
                    background: "#ffffff",
                    color: "#111827",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "none",
                    display: "inline-block",
                    position: "relative",
                    zIndex: 100,
                  }}
                >
                  Accéder
                </Link>
              )}
            </div>
            <div style={S.checkboxContainer}>
              <input
                type="checkbox"
                checked={participatingGardesReanimation}
                onClick={(e) => {
                  e.stopPropagation();
                  if (updating || loading) {
                    e.preventDefault();
                    return;
                  }
                  if (participatingGardesReanimation) {
                    e.preventDefault();
                    handleToggleParticipation("gardes-reanimation", true);
                  } else {
                    handleToggleParticipation("gardes-reanimation", false);
                  }
                }}
                onChange={() => {}}
                disabled={updating || loading}
                style={S.checkbox}
                id="checkbox-gardes-reanimation"
              />
              <label htmlFor="checkbox-gardes-reanimation" style={S.checkboxLabel}>
                Je participe à cette activité {participatingGardesReanimation ? "✓" : "✗"}
              </label>
            </div>
          </div>
          )}
        </div>
        )}

        {/* Modal de confirmation d'exclusion */}
        {showExclusionModal.open && (
          <ConfirmExclusionModal
            isOpen={showExclusionModal.open}
            onConfirm={handleConfirmExclusion}
            onCancel={() => {
              console.log("🔍 [PortailMedecin] Annulation de l'exclusion");
              setShowExclusionModal({ open: false, activity: null });
            }}
            activityName={
              showExclusionModal.activity === "oph"
                ? "Activité OPH"
                : showExclusionModal.activity === "gardes"
                ? "Garde Port Royal 2"
                : showExclusionModal.activity === "gardes-pr1"
                ? "Garde Port Royal 1"
                : showExclusionModal.activity === "gardes-reanimation"
                ? "Garde Réanimation"
                : showExclusionModal.activity === "astreinte-thoracique"
                ? "Astreinte thoracique"
                : showExclusionModal.activity === "gardes-digestif"
                ? "Garde Digestif"
                : (ACTIVITY_CONFIG[showExclusionModal.activity]?.label ?? "")
            }
          />
        )}
      </div>
    </div>
  );
}
